import { useLayoutEffect, useState } from 'react'

/** Space kept under the element: the page padding (or full-screen padding) below it. */
const BOTTOM_GAP = 18
/** Never squeeze below this; on short windows the page scrolls a little instead. */
const MIN_HEIGHT = 320

/**
 * A max-height that ends the element at the bottom of the window, so the page
 * around it needs no scrolling of its own and the element is the one scroller,
 * with its scrollbars on screen. Inside a fixed full-screen surface (marked
 * `data-fit-root`) the window's scroll position does not move it, so it is
 * measured from the viewport alone.
 */
export function useFitToWindow(
  ref: React.RefObject<HTMLElement | null>,
  enabled: boolean,
): number | undefined {
  const [maxHeight, setMaxHeight] = useState<number>()

  useLayoutEffect(() => {
    const el = ref.current
    if (!enabled || !el) return
    const measure = (): void => {
      const fixed = el.closest('[data-fit-root]') !== null
      const top = el.getBoundingClientRect().top + (fixed ? 0 : window.scrollY)
      setMaxHeight(Math.max(MIN_HEIGHT, Math.floor(window.innerHeight - top - BOTTOM_GAP)))
    }
    const frame = requestAnimationFrame(measure)
    // Anything above can change height (banners, a wrapping toolbar, templates,
    // a row appearing just above in the same container). A fixed surface does
    // not resize the body, so it and the element's own container are watched too.
    const observer = new ResizeObserver(measure)
    observer.observe(document.body)
    if (el.parentElement) observer.observe(el.parentElement)
    const fitRoot = el.closest('[data-fit-root]')
    if (fitRoot) observer.observe(fitRoot)
    window.addEventListener('resize', measure)
    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [ref, enabled])

  return enabled ? maxHeight : undefined
}
